// Dependencies
import { all, call, takeEvery, fork, put } from "@redux-saga/core/effects";
// Action Types
import {
  SUB_USER_SEARCH,
  SUB_USER_GET,
  SUB_USER_CURRENT_GET,
  SUB_USER_CREATE,
  SUB_USER_UPDATE,
  SUB_USER_DELETE,
  SUB_USER_RESET_COGNITO,
  SUB_USER_DELETE_BULK,
  SUB_USER_RESET_PASSWORD,
  SUB_USER_RESEND_INVITE,
  SUB_USER_SEARCH_ROLES
} from "Redux/actionTypes";
import { SUB_USER_AVATAR_CREATE } from "./actionTypes";

// Actions
import {
  searchSubUserResponse,
  getSubUserResponse,
  getCurrentSubUserResponse,
  createSubUserResponse,
  updateSubUserResponse,
  deleteSubUserResponse,
  resetCognitoSubUserResponse,
  resetPasswordCognitoResponse,
  resendInviteResponse,
  searchSubUserRolesResponse,
  createSubUserAvatarResponse
} from "Redux/actions";

// Helpers
import { clientQuery, clientMutation } from "Core/data/GraphQLApi";
import { endpoints } from "Core/defaultValues";
import { ExceptionManager } from "Core/logManager";
import { uploadFile } from "Core/data/FileUpload";

function* searchSubUser({ payload }) {
  const { openSearch, limit, offset } = payload;
  const query = `
    query searchSubUsers ($conditions: [SubUserSearchInput], $sort: [SubUserSortInput], $limit: Int, $offset: Int) {
      searchSubUsers (conditions: $conditions, sort: $sort, limit: $limit, offset: $offset) {
          items {
            id
            userName
            firstName
            lastName
            roleId
            customRoleId
            role {
                name
            }
            customRole {
                id
                name
            }
            tagIds
            tags {
              id
              label
            }
            email
            expiration
            phone
            registrationStatus
            avatarFileId
            avatarFile {
              id
              repositoryId
              fileKey
            }
          }
          total
      }
    }`;

  const variables = {
    "conditions": [
      {
        "fields": [
          "firstName",
          "userName",
          "email"
        ],
        "exact": false,
        "terms": [
          openSearch
        ]
      }
    ],
    "sort":[
      {
        "field": "firstName",
	      "order": "ASC"
      }
    ],
    "limit": limit,
    "offset": offset
  };
  
  try {
    const data = yield call(
      clientQuery,
      query,
      variables,
      endpoints.GRAPHQL_GENERAL_V2
    );
    if (data) {
      const { searchSubUsers: { items, total } } = data;
      yield put(searchSubUserResponse({ items, total, error: null }));
    }
  } catch (exc) {
    yield put(searchSubUserResponse({
      error: exc.error,
      items: [],
      total: 0
    }));
    ExceptionManager(
      exc,
      "modules/account/views/subUser/redux/sagas",
      "searchSubUser"
    );
  }
}

function* getSubUser({ payload }) {
  const { id } = payload;
  const query = `
    query getSubUser ($id: Int) {
      getSubUser (id: $id) {
        id
        userName
        firstName
        lastName
        roleId
        customRoleId
        role {
            id
            name
            description
            modules {
                id
                code
                userCode
                name
                category
                subModules {
                    id
                    userCode
                    name
                    groupName
                    parentModuleId
                    category
                }
            }
            isCustom
            customOwner
            isSupervisor
        }
        customRole {
            id
            name
            description
            isSupervisor
            modules {
                id
                code
                userCode
                name
                groupName
                parentModuleId
                category
                subModules {
                    id
                    userCode
                    name
                    groupName
                    parentModuleId
                    category
                }
            }
            isCustom
            customOwner
        }
        tagIds
        email
        activeTab
        expiration
        phone
        avatarFileId
        avatarFile {
          id
          repositoryId
          fileKey
        }
        eldUser {
          username
          password
        }
      }
    }`;

  const variables = { id };

  try {
    const data = yield call(
      clientQuery,
      query,
      variables,
      endpoints.GRAPHQL_GENERAL
    );
    if (data) {
      const { getSubUser } = data;
      yield put(getSubUserResponse({ getSubUser }));
    }
  } catch (exc) {
    yield put(getSubUserResponse({ error: exc.error }));
    ExceptionManager(
      exc,
      "modules/account/views/subUser/redux/sagas",
      "getSubUser"
    );
  }
}

function* getCurrentSubUser({ payload }) {
  const { id } = payload;
  const query = `
    query getSubUser ($id: Int) {
      getSubUser (id: $id) {
        id
        roleId
        customRoleId
        role {
            id
            isSupervisor
        }
        customRole {
            id
            isSupervisor
        }
      }
    }`;

  const variables = { id };

  try {
    const data = yield call(
      clientQuery,
      query,
      variables,
      endpoints.GRAPHQL_GENERAL
    );
    if (data) {
      const { getSubUser } = data;
      yield put(getCurrentSubUserResponse({ getSubUser }));
    }
  } catch (exc) {
    yield put(getCurrentSubUserResponse({ error: exc.error }));
    ExceptionManager(
      exc,
      "modules/account/views/subUser/redux/sagas",
      "getCurrentSubUser"
    );
  }
}

function* createSubUser({ payload }) {
  const {
    firstName,
    lastName,
    userName,
    password,
    expiryDate,
    roleId,
    tagIds,
    roleModules,
    customRole,
    phone,
    email,
    eldUser,
    isSupervisor = 0,
    avatarFileId
  } = payload;

  const mutation = `
      mutation createSubUser ($userName: String!, $firstName: String!,$lastName: String!, $roleId: Int!, $customRole: Boolean, $languageId: Int, $email: String!, $tagIds: [Int]!, $activeTab: String, $expiryDate: String, $roleModules: [Int], $phone: String, $eldUser: EldUserInput, $isSupervisor: Boolean, $avatarFileId: Int) {
        createSubUser (userName: $userName,firstName: $firstName, lastName: $lastName, roleId: $roleId, customRole: $customRole, languageId: $languageId, email: $email, tagIds: $tagIds, activeTab: $activeTab, expiryDate: $expiryDate, roleModules: $roleModules, phone: $phone, eldUser: $eldUser, isSupervisor: $isSupervisor, avatarFileId: $avatarFileId) {
          id
          firstName
          lastName
          roleId
          customRoleId
          role {
              id
              name
              description
              modules {
                  id
                  code
                  userCode
                  name
                  category
                  subModules {
                      id
                      userCode
                      name
                      groupName
                      parentModuleId
                      category
                  }
              }
              isCustom
              customOwner
              isSupervisor
          }
          customRole {
              id
              name
              description
              modules {
                  id
                  code
                  userCode
                  name
                  groupName
                  parentModuleId
                  category
                  subModules {
                      id
                      userCode
                      name
                      groupName
                      parentModuleId
                      category
                  }
              }
              isCustom
              customOwner
              isSupervisor
          }
          tagIds
          email
          activeTab
          expiration
          phone
          avatarFileId
          eldUser {
            username
            password
          }
        }
    }`;

  const variables = {
    userName,
    firstName,
    lastName,
    password,
    expiryDate,
    roleId,
    tagIds,
    roleModules,
    customRole,
    phone,
    email,
    eldUser,
    isSupervisor,
    avatarFileId
  };

  try {
    const data = yield call(clientMutation, mutation, variables, endpoints.GRAPHQL_GENERAL_V2);
    if (data) {
      yield put(createSubUserResponse({
        newSubUser: data.createSubUser ? true : false,
        error: null
      }));
    } else {
      throw 'SubUser has not been created';
    }
  } catch (exc) {
    const customExceptionMessage = "CustomException: ";
    const message = exc?.error?.replace(customExceptionMessage, "") || "";
    yield put(
      createSubUserResponse({
        error: message,
        newSubUser: false
      })
    );
    
    exc.message = message;
    exc.error = message;
    
    exc.errorData.errors.map(error => {
      error.message = error?.message?.replace(customExceptionMessage, "") || "";
      return;
    })
    ExceptionManager(
      exc,
      "modules/account/views/SUB_USER/redux/sagas",
      "createSubUser",
      true,
    );
  }
}

function* updateSubUser({ payload }) {
  const {
    id,
    firstName,
    lastName,
    expiryDate,
    roleId,
    tagIds,
    customRole,
    roleModules,
    phone,
    eldUser,
    isSupervisor = 0,
    avatarFileId
  } = payload;

  const mutation = `
    mutation updateSubUser ($id: Int!, $firstName: String, $lastName: String, $roleId: Int, $customRole: Boolean, $languageId: Int, $tagIds: [Int], $activeTab: String, $expiryDate: String, $roleModules: [Int], $phone: String, $eldUser: EldUserInput, $isSupervisor: Boolean, $avatarFileId: Int) {
      updateSubUser (id: $id, firstName: $firstName, lastName: $lastName, roleId: $roleId, customRole: $customRole, languageId: $languageId, tagIds: $tagIds, activeTab: $activeTab, expiryDate: $expiryDate, roleModules: $roleModules, phone: $phone, eldUser: $eldUser, isSupervisor: $isSupervisor, avatarFileId: $avatarFileId) {
        id
        userName
        firstName
        lastName
        roleId
        customRoleId
        role {
            id
            name
            description
            modules {
                id
                code
                userCode
                name
                category
                subModules {
                    id
                    userCode
                    name
                    groupName
                    parentModuleId
                    category
                }
            }
            isCustom
            customOwner
            isSupervisor
        }
        customRole {
            id
            name
            description
            
            modules {
                id
                code
                userCode
                name
                groupName
                parentModuleId
                category
                subModules {
                    id
                    userCode
                    name
                    groupName
                    parentModuleId
                    category
                }
            }
            isCustom
            customOwner
            isSupervisor
        }
        tagIds
        activeTab
        expiration
        phone
        avatarFileId
        eldUser {
          username
          password
        }
      }
    }`;

  const variables = {
    id,
    firstName,
    lastName,
    expiryDate,
    roleId,
    tagIds,
    customRole,
    roleModules,
    phone,
    eldUser,
    isSupervisor,
    avatarFileId
  };

  try {
    const data = yield call(clientMutation, mutation, variables, endpoints.GRAPHQL_GENERAL_V2);
    if (data) {
      yield put(updateSubUserResponse({
        updateSubUser: data.updateSubUser ? true : false,
        error: null
      }));
    } else {
      updateSubUserResponse({
        error: "subUser_subUser_notUpdated",
        updateSubUser: false
      })
    }
  } catch (exc) {
    yield put(
      updateSubUserResponse({
        error: exc.error,
        updateSubUser: false
      })
    );
    ExceptionManager(
      exc,
      "modules/account/views/SubUser/redux/sagas",
      "updateSubUser",
      true,
    );
  }
}

function* deleteSubUser({ payload }) {
  try {
    const { items } = payload;
    let mutations = [];

    items.forEach((item, index) => {
      mutations.push(`SubUser${index}: deleteSubUser(id: ${item?.id})`)
    });

    let mutation = `mutation{${mutations.join("")}}`;

    const response = yield call(
      clientMutation,
      mutation,
      {},
      endpoints.GRAPHQL_GENERAL_V2,
    );

    if (response && response.SubUser0) {
      yield put(deleteSubUserResponse({ deleteSubUser: response, error: null }));
    } else {
      throw 'Error while deleting user';
    }
  } catch (exc) {
    yield put(
      deleteSubUserResponse({
        error: exc.error,
        deleteSubUser: false
      })
    );
    ExceptionManager(
      exc,
      "modules/account/views/SubUser/redux/sagas",
      "deleteSubUser",
      true,
    );
  }
}

function* resetCognitoSubUser({ payload }) {
  const { id, email } = payload;

  const mutation =
    `mutation resetCognitoSubUser ($userId: Int!, $email: String!, $newUsername: String) {
      resetCognitoSubUser (userId: $userId, email: $email, newUsername: $newUsername)
    }`;

  const variables = {
    userId: id,
    newUsername: "",
    email
  };

  try {
    const data = yield call(clientMutation, mutation, variables, endpoints.GRAPHQL_GENERAL_V2);
    if (data) {
      yield put(resetCognitoSubUserResponse({ cognitoReset: data.resetCognitoSubUser }));
    } else {
      resetCognitoSubUserResponse({
        error: "subUser_subUser_notUpdated",
        cognitoReset: false
      })
    }
  } catch (exc) {
    yield put(
      resetCognitoSubUserResponse({
        error: exc.error,
        cognitoReset: false
      })
    );
    ExceptionManager(
      exc,
      "modules/account/views/SubUser/redux/sagas",
      "resetCognitoSubUser",
      true
    );
  }
}

function* deleteSubUsers({ payload }) {
  const { ids } = payload;

  const mutation =
    `mutation deleteSubUsers ($ids: [Int]!) {
      deleteSubUsers (ids: $ids)
    }`;

  const variables = {
    ids
  };

  try {
    const data = yield call(clientMutation, mutation, variables, endpoints.GRAPHQL_GENERAL_V2);
    if (data) {
      yield put(deleteSubUserResponse({ deleteSubUser: data.deleteSubUsers, error: null }));
    } else {
      throw 'Error while deleting user';
    }
  } catch (exc) {
    yield put(
      deleteSubUserResponse({
        error: exc.error,
        deleteSubUser: false
      })
    );
    ExceptionManager(
      exc,
      "modules/account/views/SubUser/redux/sagas",
      "deleteSubUsers"
    );
  }
}

function* resetPasswordSubUser({ payload }) {
  const { userId } = payload;

  const mutation =
    `mutation resetPasswordSubUser ($userId: Int!) {
      resetPasswordSubUser (userId: $userId)
    }`;

  const variables = {
    userId
  };

  try {
    const data = yield call(clientMutation, mutation, variables, endpoints.GRAPHQL_GENERAL_V2);
    if (data && data?.resetPasswordSubUser) {
      yield put(resetPasswordCognitoResponse({
        result: data.resetPasswordSubUser,
        error: null
      }));
    } else {
      resetPasswordCognitoResponse({
        result: false,
        error: "subUser_subUser_notUpdated"
      })
    }
  } catch (exc) {
    yield put(
      resetPasswordCognitoResponse({
        result: false,
        error: exc.error
      })
    );
    ExceptionManager(
      exc,
      "modules/account/views/SubUser/redux/sagas",
      "resetPasswordSubUser",
      true
    );
  }
}

function* resendInviteSubUser({ payload }) {
  const { id } = payload;

  const mutation =
    `mutation resendInvite ($id: Int!) {
      resendInvite (id: $id)
    }`;

  const variables = {
    id
  };

  try {
    const data = yield call(clientMutation, mutation, variables, endpoints.GRAPHQL_GENERAL_V2);
    if (data) {
      yield put(resendInviteResponse({
        result: data.resendInvite,
        error: null
      }));
    } else {
      resendInviteResponse({
        result: false,
        error: exc.error
      })
    }
  } catch (exc) {
    yield put(
      resendInviteResponse({
        result: false,
        error: exc.error
      })
    );
    ExceptionManager(
      exc,
      "modules/account/views/SubUser/redux/sagas",
      "resetPasswordSubUser",
      true
    );
  }
}

function* searchSubUserRoles({ payload }) {
  const { openSearch, limit, offset } = payload;
  const query = `
    query searchRoles ($openSearch: String, $limit: Int, $offset: Int) {
      data: searchRoles (openSearch: $openSearch, limit: $limit, offset: $offset) {
          items {
            id
            name
            description
            isSupervisor
          }
          total
      }
    }
  `;

  const variables = {
    openSearch,
    limit,
    offset
  };

  try {
    const response = yield call(
      clientQuery,
      query,
      variables,
      endpoints.GRAPHQL_GENERAL
    );
    if (response) {
      const { data: { items, total } } = response;
      yield put(searchSubUserRolesResponse({ items, total, error: null }));
    }
  } catch (exc) {
    yield put(searchSubUserRolesResponse({
      items: [],
      total: 0
    }));
    ExceptionManager(
      exc,
      "modules/account/views/SubUser/redux/sagas",
      "searchSubUserRoles"
    );
  }

}

function* createSubUserAvatar({ payload }) {
  const { file, fileKeyDelete } = payload;

  try {
    // Delete old avatar file if provided
    if (fileKeyDelete) {
      const mutation = `mutation deleteAttachment ($id: Int!) {deleteAttachment (id: $id)}`;
      yield call(clientMutation, mutation, { id: fileKeyDelete }, endpoints.GRAPHQL_GENERAL_V2);
    }

    // Upload new avatar file if provided
    if (file) {
      // Get file extension
      let extension = file?.name.split(".").pop();
      
      // Get presigned URL for upload
      let presignedUrlResponse = yield call(
        clientQuery,
        `query(
          $repositoryCode: String
          $fileExtension: String
        ) {
          Data: getPresignedUrl(
            repositoryCode: $repositoryCode,
            fileExtension: $fileExtension
          ) {
            repositoryCode
            fileKey
            url
          }
        }`,
        {
          repositoryCode: endpoints.REPOSITORY_CODE,
          fileExtension: extension,
        },
        endpoints.GRAPHQL_GENERAL_V2
      );

      if (!presignedUrlResponse || !presignedUrlResponse.Data) {
        throw "There is a problem uploading the file";
      }

      // Upload file to S3
      yield call(uploadFile, presignedUrlResponse.Data.url, file);

      // Register attachment
      let registerAttachmentResult = yield call(
        clientMutation,
        `mutation ($repositoryCode: String!, $fileKey: String!, $originalName: String){
          attachment: registerAttachment(repositoryCode: $repositoryCode, fileKey: $fileKey, originalName: $originalName){
            id
            repositoryId
            repositoryType
            repositoryCode
            repositoryPath
            fileKey
            originalName
          }
        }`,
        {
          repositoryCode: presignedUrlResponse.Data.repositoryCode,
          fileKey: presignedUrlResponse.Data.fileKey,
          originalName: file?.name,
        },
        endpoints.GRAPHQL_GENERAL
      );

      if (registerAttachmentResult) {
        yield put(createSubUserAvatarResponse({
          attachment: registerAttachmentResult.attachment,
          error: null
        }));
      } else {
        throw "There is a problem registering the file";
      }
    } else {
      // No file to upload, just return null
      yield put(createSubUserAvatarResponse({
        attachment: { id: null, fileKey: fileKeyDelete },
        error: null
      }));
    }
  } catch (exc) {
    yield put(createSubUserAvatarResponse({
      attachment: null,
      error: exc?.error || "Avatar upload failed"
    }));
    ExceptionManager(
      exc,
      "modules/account/views/SubUser/redux/sagas",
      "createSubUserAvatar",
      true
    );
  }
}

function* watchMethods() {
  yield takeEvery(SUB_USER_SEARCH, searchSubUser);
  yield takeEvery(SUB_USER_GET, getSubUser);
  yield takeEvery(SUB_USER_CURRENT_GET, getCurrentSubUser);
  yield takeEvery(SUB_USER_CREATE, createSubUser);
  yield takeEvery(SUB_USER_UPDATE, updateSubUser);
  yield takeEvery(SUB_USER_DELETE, deleteSubUser);
  yield takeEvery(SUB_USER_RESET_COGNITO, resetCognitoSubUser);
  yield takeEvery(SUB_USER_DELETE_BULK, deleteSubUsers);
  yield takeEvery(SUB_USER_RESET_PASSWORD, resetPasswordSubUser);
  yield takeEvery(SUB_USER_RESEND_INVITE, resendInviteSubUser);
  yield takeEvery(SUB_USER_SEARCH_ROLES, searchSubUserRoles);
  yield takeEvery(SUB_USER_AVATAR_CREATE, createSubUserAvatar);
}
export default function* rootSaga() {
  yield all([fork(watchMethods)]);
}